import React from "react";
import { FcGoogle } from "react-icons/fc";
import {
    SiInstagram,
    SiTiktok,
    SiFacebook,
    SiWhatsapp,
    SiYoutube,
    SiX,
    SiSnapchat,
    SiTelegram,
    SiPinterest,
    SiSpotify,
} from "react-icons/si";
import {
    Link as LinkIcon,
    Globe,
    Phone,
    Mail,
    Calendar,
    ShoppingBag,
    Camera,
    Coffee,
    Utensils,
    Gift,
    Heart,
    Star,
    Sparkles,
    MessageCircle,
    Send,
    ExternalLink,
    Navigation,
} from "lucide-react";

// -----------------------------------------------------------------------------
// Custom multi-color Google Maps pin (matches Google Maps brand)
// -----------------------------------------------------------------------------
const GoogleMapsPin = ({ className, size }) => (
    <svg
        className={className}
        width={size}
        height={size}
        viewBox="0 0 384 512"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
    >
        <path fill="#1a73e8" d="M192 0C86 0 0 86 0 192c0 77.4 27 99.1 172.3 309.7 9.5 13.8 30 13.8 39.4 0C357 291.1 384 269.4 384 192 384 86 298 0 192 0z"/>
        <path fill="#ea4335" d="M192 0C123 0 61.9 36.4 27.5 91.6L184 274.6l17 5.4h30l64-64V192c0-30.9-7.3-60.1-20.3-86-8.1-16.3-19-31.2-32.4-44.1C219 22.9 206.5 12 192 0z"/>
        <path fill="#fbbc04" d="M27.5 91.6C10 119.7 0 154.7 0 192c0 37.3 10 72.3 27.5 100.4L184 274.6z"/>
        <path fill="#34a853" d="M362.5 91.6c-6.3-10-13.5-19.4-21.6-27.9C305.7 24.5 251.9 0 192 0v128c0 35.3-28.7 64-64 64H64c-35.3 0-64 28.7-64 64 0 55.2 43 100.2 100.2 100.2h91.8L215 384h72c53 0 96-43 96-96V192c0-37.3-10-72.3-27.5-100.4z"/>
        <circle fill="#fff" cx="192" cy="192" r="60"/>
    </svg>
);

// -----------------------------------------------------------------------------
// Registry: each entry describes how to render an icon and its brand color.
// -----------------------------------------------------------------------------
export const ICONS = {
    google_reviews: { label: "Google Reviews", type: "fc-google" },
    google_maps: { label: "Google Maps", type: "gmap" },
    instagram: { label: "Instagram", type: "si", C: SiInstagram, color: "#E1306C" },
    tiktok: { label: "TikTok", type: "si", C: SiTiktok, color: "#000000" },
    facebook: { label: "Facebook", type: "si", C: SiFacebook, color: "#1877F2" },
    whatsapp: { label: "WhatsApp", type: "si", C: SiWhatsapp, color: "#25D366" },
    youtube: { label: "YouTube", type: "si", C: SiYoutube, color: "#FF0000" },
    x: { label: "X / Twitter", type: "si", C: SiX, color: "#000000" },
    telegram: { label: "Telegram", type: "si", C: SiTelegram, color: "#26A5E4" },
    snapchat: { label: "Snapchat", type: "si", C: SiSnapchat, color: "#FFFC00" },
    pinterest: { label: "Pinterest", type: "si", C: SiPinterest, color: "#BD081C" },
    spotify: { label: "Spotify", type: "si", C: SiSpotify, color: "#1DB954" },

    // Generic (lucide) icons
    menu: { label: "Menu", type: "lucide", C: Utensils, color: "#111827" },
    website: { label: "Website", type: "lucide", C: Globe, color: "#111827" },
    phone: { label: "Phone", type: "lucide", C: Phone, color: "#111827" },
    email: { label: "Email", type: "lucide", C: Mail, color: "#111827" },
    booking: { label: "Booking", type: "lucide", C: Calendar, color: "#111827" },
    shop: { label: "Shop", type: "lucide", C: ShoppingBag, color: "#111827" },
    camera: { label: "Photos", type: "lucide", C: Camera, color: "#111827" },
    coffee: { label: "Café", type: "lucide", C: Coffee, color: "#6F4E37" },
    utensils: { label: "Restaurant", type: "lucide", C: Utensils, color: "#111827" },
    star: { label: "Reviews", type: "lucide", C: Star, color: "#F59E0B" },
    gift: { label: "Gift", type: "lucide", C: Gift, color: "#111827" },
    heart: { label: "Favorite", type: "lucide", C: Heart, color: "#DC2626" },
    sparkles: { label: "Special", type: "lucide", C: Sparkles, color: "#F59E0B" },
    message: { label: "Message", type: "lucide", C: MessageCircle, color: "#111827" },
    send: { label: "Send", type: "lucide", C: Send, color: "#111827" },
    map: { label: "Location", type: "lucide", C: Navigation, color: "#111827" },
    external: { label: "External", type: "lucide", C: ExternalLink, color: "#111827" },
    link: { label: "Link", type: "lucide", C: LinkIcon, color: "#111827" },
};

// -----------------------------------------------------------------------------
// Renderer — always sized via className. Multi-color icons ignore color prop.
// -----------------------------------------------------------------------------
export function BrandIcon({ name, className = "h-6 w-6", size, style }) {
    const meta = ICONS[name] || ICONS.link;
    const px = size || 24;
    if (meta.type === "fc-google") {
        return <FcGoogle className={className} size={px} style={style} />;
    }
    if (meta.type === "gmap") {
        return <GoogleMapsPin className={className} size={px} />;
    }
    const C = meta.C;
    return (
        <C
            className={className}
            size={px}
            color={meta.color}
            style={style}
        />
    );
}

export function iconLabel(name) {
    return (ICONS[name] || ICONS.link).label;
}
